// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — minerium.js   ·   THE HOLDING ENGINE
// ────────────────────────────────────────────────────────────────────
// Your own Minerium: the yard outside the Hall of Deeds, everything
// standing in it, everything growing in it, the sky over it and the
// companion following you around it.
//
// ⚑ NO REACT IN THIS FILE. Schema, clock, placement maths, growth
// maths, and the Firebase writers. The renderer imports this; this
// imports no renderer. Same law `pierCamera.js` is built to:
// logic → data ← renderer.
//
// ── THE NAME ────────────────────────────────────────────────────────
// caP'n: "it's not just a house anymore, it's our very own Minerium."
// That word is already Terror's, and it fits exactly: a Minerium is
// somebody's own holding — `Terror Grimoire/TERROR_ROSTER.md` opens
// "THE MINERIUM OF SIR H'OSH VROWN" and speaks of "one single
// Minerium" as a place with its own roster. Sir H'osh Vrown has his.
// This one is yours.
//
// ── FIREBASE ────────────────────────────────────────────────────────
//   homes/{ownerUid}/minerium/props/{propId}    = { itemId, area, x, y, rot, on? }
//     ⚠ `area` is "yard" | "hall", and they are TWO SEPARATE TILE
//       SPACES that both start at (0,0) — not two regions of one map.
//       Every bounds test, collision test and renderer takes the area;
//       anything that forgets to will quietly mix the two rooms.
//   homes/{ownerUid}/minerium/growers/{propId}  = { plantedAt, collectedAt }
//   homes/{ownerUid}/minerium/sky               = itemId
//   homes/{ownerUid}/minerium/ground            = itemId
//   homes/{ownerUid}/minerium/companion         = { beingId, tier }
//   homes/{ownerUid}/minerium/hallFloor | hallWall = itemId   (worn finishes, 2026-09-15)
//   homes/{ownerUid}/minerium/hallLight          = itemId   (the Hall's mood, OW-34)
//   homes/{ownerUid}/minerium/interior | designs      (the Hall Architect, OW-42 — hallInterior.js)
//   profiles/{uid}/minerium/stock/{itemId}      = n     (mineriumShop.js owns this)
//
// ⚠ IT IS A SIBLING OF `homes/{ownerUid}/decor`, NOT A CHILD OF IT, AND
//   THAT IS A DELIBERATE DEPARTURE FROM THE SEAM COMMENT IN PierHome.js.
//   That comment (written before the Minerium existed) planned for
//   furnishings to share the decor node under a slot-id prefix. Two
//   reasons it goes elsewhere instead, both worth the deviation:
//     1 · HALL DECOR LAW (PIERRE_PRIME L21) says a frame stores ONLY a
//         pieceId — "a pointer, not a copy" — and the whole node is
//         read as that. Growth timestamps, rotations and stack parents
//         do not belong under a law that says its values are pointers.
//     2 · `useHallDecor` builds `hungPieceIds` from
//         `Object.values(decorMap)`. Objects living in that node would
//         end up in a Set of pieceId strings. Harmless today, and
//         exactly the kind of harmless that becomes a bug in a year.
//   Instancing, rules and visibility are identical either way — both
//   sit under `homes/{ownerUid}`, both are owner-write, both are read
//   by visitors. Nothing is lost by the move. ⚑ The seam comment in
//   PierHome.js is amended in place to point here, per the rule that a
//   doc known-wrong is worse than no doc.
// ════════════════════════════════════════════════════════════════════

import {
  ref, onValue, push, update, remove, get, runTransaction,
  serverTimestamp, increment,
} from "firebase/database";
import { db, auth } from "./firebase";
import {
  ITEMS, KIND, AREAS, SHELVES, isWorn, FOUNDING_SKY, FOUNDING_GROUND, FOUNDING_FLOOR, FOUNDING_WALL,
  FOUNDING_LIGHT, LAYER, layerOf, sizeOf, turnsInQuarters,
} from "./mineriumCatalogue";
import { grantDawgs, DAWGS_GRANT_CEILING } from "./upDawgs";

// ════════════════════════════════════════════════════════════════════
// §1 · THE GROUND YOU STAND ON
// ════════════════════════════════════════════════════════════════════
// Tile space, same convention as the pier and the Hall: +x east,
// +y south. The renderer maps tile y onto world z.
export const YARD = {
  cols: 26,
  rows: 20,
  // the walkable rectangle inside the fence
  x0: 1, y0: 1, x1: 24, y1: 18,
  spawn: { x: 12.5, y: 16 },
};

export const inYard = (x, y) =>
  x >= YARD.x0 && x <= YARD.x1 && y >= YARD.y0 && y <= YARD.y1;

// ⭐ THE HALL — OW-24, 2026-09-13. The other half of a holding.
// ⚠ THIS IS A BACKSTOP, NOT THE AUTHORITY. `PierHome.js` computes the
// real walkable floor from its wing layout (`isWalkable`), the hall grows
// wider every time a title is added to the Empire, and the build UI in
// that file checks every tile against it before calling `placeProp`. So
// what this rectangle exists for is the OTHER failure: a malformed or
// hand-written row putting a chair at y = -500, which no UI is standing
// in front of. Generous on x for exactly that reason — tightening it
// would break the Hall the day a fifth wing ships, silently, months from
// now, and a bound that goes stale on its own is worse than a loose one.
export const HALL = { x0: 0, y0: 1, x1: 128, y1: 64 };

export const inHall = (x, y) =>
  x >= HALL.x0 && x <= HALL.x1 && y >= HALL.y0 && y <= HALL.y1;

// One question, one answer, for whichever ground you are standing on.
export const inArea = (area, x, y) =>
  area === AREAS.HALL ? inHall(x, y) : inYard(x, y);

// ⭐ FREE PLACEMENT, BUT NOT FLOAT SOUP. caP'n asked for "placing things
// where you want, how you want." A half-tile snap is what that means in
// practice: fine enough that nothing feels gridded, coarse enough that
// two objects can actually be told apart and a collision test is exact
// rather than a float comparison that is right 99% of the time.
export const SNAP = 0.5;
export const snap = (v) => Math.round(v / SNAP) * SNAP;

// Eight-step rotation. A free angle looks worse, not better — nothing
// in this art style has a silhouette that reads at 7°.
export const ROT_STEP = Math.PI / 4;
export const ROT_STEPS = 8;
// ⚑ THE HALL TURNS IN QUARTERS. Its floor is an integer grid and its
// collision is a square footprint that does not rotate, so a bench at 45°
// pokes a corner out of the tiles it claims. Four steps of the same
// ROT_STEP unit, so `moveProp`'s normalisation is unchanged.
// ⭐ OW-34: and so does any piece that is not square, or hangs on a wall,
// in either room — pass the itemId and the answer includes it.
export const rotStepFor = (area, itemId = null) =>
  (area === AREAS.HALL || (itemId && turnsInQuarters(itemId)) ? Math.PI / 2 : ROT_STEP);

// ⭐ THE SHAPE A PIECE COVERS, TURNED (OW-34, 2026-09-16).
// `dimsOf(itemId, rot)` → { w, d } in tiles: w along x, d along y. A
// quarter turn swaps them. A square piece at 45° keeps its square — the
// collision test has always treated it that way and nothing standing
// today changes.
const quarterOf = (rot) => ((Math.round((Number(rot) || 0) / (Math.PI / 2)) % 4) + 4) % 4;
export function dimsOf(itemId, rot = 0) {
  const [w, d] = sizeOf(itemId);
  return quarterOf(rot) % 2 ? { w: d, d: w } : { w, d };
}
// A quarter-turn piece's rot, snapped to the nearest quarter.
export const quarterRot = (rot) => quarterOf(rot) * (Math.PI / 2);
// Every function below that takes a footprint takes EITHER the old number
// (a square) OR `{ w, d }`. This is the one place that reads either.
export function asDims(fp) {
  if (fp && typeof fp === "object") {
    if (Array.isArray(fp)) return { w: Math.max(1, fp[0] || 1), d: Math.max(1, fp[1] || 1) };
    return { w: Math.max(1, fp.w || 1), d: Math.max(1, fp.d || 1) };
  }
  const n = Math.max(1, Number(fp) || 1);
  return { w: n, d: n };
}

// ⭐ THE REACH — the ONE function that says where a held piece lands and
// where "pick up" looks, for both rooms and both renderers.
// (2026-09-15) It was written FOUR times: `ghostSpot` in MineriumScreen,
// the useFrame in Minerium3D's Ghost, `facingTile` in PierHome and the
// useFrame in Hall3D's Ghost — and two of the four had `(f.y || 1)`, which
// is 1 when facing EAST or WEST (y = 0 is falsy), so the yard's ghost sat
// a tile SOUTH-east of you instead of east. The collision test read the
// same wrong tile, so nothing ever complained: the ring was green on a
// spot the player was not looking at.
//   yard — 1.1 tiles ahead, half-tile snapped (free placement)
//   hall — the tile you face, integer (the Hall walks a tile at a time)
// ⚑ EVEN FOOTPRINTS SIT ON A TILE CORNER in the hall. A 2-wide piece
// centred on a tile centre spans three tiles (half of each neighbour);
// centred on the corner between four tiles it covers exactly those four.
// So a footprint-2 piece lands at reach + 0.5 along BOTH axes, toward the
// way you face — `tilesUnder(6.5, 6.5, 2)` is exactly (6..7, 6..7).
// ⭐ OW-34 · A LONG PIECE STARTS AT THE TILE YOU FACE AND RUNS AWAY FROM
// YOU. Centred on the reach tile, a 3-deep runner faced end-on covered the
// tile you stand on and the one behind you. So along the way you face the
// centre moves out by (extent − 1) / 2; across it, an even width still
// sits on the tile corner. `footprint` is a number or `{ w, d }` (turned).
export function reachTile(area, pos, facing, footprint = 1) {
  const fx = facing?.x || 0;
  const fy = facing?.y || 0;
  const dx = fx || fy ? fx : 0, dy = fx || fy ? fy : 1;   // not yet turned: south
  const { w, d } = asDims(footprint);
  const along = dx ? w : d;                               // extent in the way you face
  const out = (along - 1) / 2;
  if (area === AREAS.HALL) {
    let x = Math.round(pos.x) + dx * (1 + out), y = Math.round(pos.y) + dy * (1 + out);
    // across the facing axis, an even extent sits on a tile corner
    if (!dx && w % 2 === 0) x += 0.5;
    if (!dy && d % 2 === 0) y += 0.5;
    return { x, y };
  }
  return { x: snap(pos.x + dx * (1.1 + out)), y: snap(pos.y + dy * (1.1 + out)) };
}

// ════════════════════════════════════════════════════════════════════
// §2 · ⭐ THE CLOCK
// ────────────────────────────────────────────────────────────────────
// ⚠⚠ GROWTH IS NEVER TIMED BY `Date.now()` ALONE. A player's system
// clock is a number they can change, and a farm timed off it is a farm
// that pays out as fast as somebody can drag the Windows clock forward.
//
// Firebase publishes `.info/serverTimeOffset` — the difference between
// this device and the database's own clock — and keeps it live. Every
// timestamp WRITTEN here is `serverTimestamp()`, resolved by the
// server; every timestamp READ is compared against `serverNow()`.
// Moving the system clock moves the offset by the same amount in the
// opposite direction and nothing changes at all.
//
// ⚑ This is a CLIENT-SIDE honesty measure, not a seal. The real seal is
// Firebase rules, and it lands with the rules-hardening session (OW-9)
// exactly as the code-before-rules law requires: ship the code, confirm
// it runs, then tighten. What this buys today is that the ordinary
// accident — a laptop with the wrong date — cannot mint Dawgs.
// ════════════════════════════════════════════════════════════════════
let _offset = 0;
let _offsetBound = false;

export function bindServerClock() {
  if (_offsetBound) return () => {};
  _offsetBound = true;
  const off = onValue(ref(db, ".info/serverTimeOffset"), (snap) => {
    const v = Number(snap.val());
    if (Number.isFinite(v)) _offset = v;
  });
  return () => { off(); _offsetBound = false; };
}

export const serverNow = () => Date.now() + _offset;
export const serverOffset = () => _offset;

// ════════════════════════════════════════════════════════════════════
// §3 · PATHS
// ════════════════════════════════════════════════════════════════════
export const holdingPath = (ownerUid) => `homes/${ownerUid}/minerium`;
const propsPath   = (u) => `${holdingPath(u)}/props`;
const growersPath = (u) => `${holdingPath(u)}/growers`;
const stockPath   = (u) => `profiles/${u}/minerium/stock`;

// ════════════════════════════════════════════════════════════════════
// §4 · READ THE HOLDING
// ────────────────────────────────────────────────────────────────────
// One listener on the whole holding node. It is a small object — a few
// dozen props at most — and one subscription beats five, especially on
// a screen running a 60fps loop (ie-realtime-perf: a Firebase listener
// per subsystem is how a smooth screen becomes a stuttering one).
// ════════════════════════════════════════════════════════════════════
export function listenToHolding(ownerUid, cb) {
  if (!ownerUid) { cb(emptyHolding()); return () => {}; }
  const off = onValue(ref(db, holdingPath(ownerUid)), (snap) => {
    cb(normaliseHolding(snap.val()));
  });
  return () => off();
}

export function emptyHolding() {
  return { props: {}, growers: {}, sky: FOUNDING_SKY, ground: FOUNDING_GROUND,
           hallFloor: FOUNDING_FLOOR, hallWall: FOUNDING_WALL, hallLight: FOUNDING_LIGHT, companion: null,
           interiorRaw: null, designsRaw: null };
}

// ⚠ EVERY ROW IS FILTERED THROUGH THE CATALOGUE ON THE WAY IN. A prop
// whose item has been renamed or retired stops rendering rather than
// throwing, and its row is left untouched in Firebase so a rename
// destroys nothing. A holding must never be able to white-screen on one
// bad row — it is somebody's house.
export function normaliseHolding(raw) {
  const out = emptyHolding();
  if (!raw || typeof raw !== "object") return out;

  for (const [propId, p] of Object.entries(raw.props || {})) {
    if (!p || !ITEMS[p.itemId] || isWorn(p.itemId)) continue;
    out.props[propId] = {
      propId,
      itemId: p.itemId,
      area: p.area === AREAS.HALL ? AREAS.HALL : AREAS.YARD,
      x: Number(p.x) || 0,
      y: Number(p.y) || 0,
      rot: Number(p.rot) || 0,
      on: typeof p.on === "string" ? p.on : null,
    };
  }

  // ⚠ A STACK PARENT THAT NO LONGER EXISTS IS AN ORPHAN, AND AN ORPHAN
  // IS DROPPED TO THE FLOOR, NEVER HIDDEN. If the thing you stacked a
  // torch on gets picked up, the torch must not vanish — it lands where
  // the parent was. `pickUp` already re-parents children, so this is
  // the belt to that braces; it exists because "your torch disappeared"
  // is unrecoverable and "your torch fell over" is not.
  for (const p of Object.values(out.props)) {
    if (p.on && !out.props[p.on]) p.on = null;
  }
  // ⚠⚠ OW-34 · A CHAIR STACKED ON A RUG BEFORE RUGS WERE FLOOR IS NOW A
  // CHAIR STANDING ON A RUG. Left as a stack it was invisible to every
  // collision test (stacked rows are skipped, and the rug no longer
  // collides with furniture), so a second chair could be put inside it,
  // it could not be moved, and undoing its pick-up failed. It takes its
  // base's spot and stands on its own. The Firebase row is healed the
  // moment the rug is moved or lifted (`healFloorChildren`).
  for (const p of Object.values(out.props)) {
    const base = p.on ? out.props[p.on] : null;
    if (base && layerOf(base.itemId) !== LAYER.STAND) {
      p.x = base.x; p.y = base.y; p.on = null; p.legacyOn = base.propId;
    }
  }

  for (const [propId, g] of Object.entries(raw.growers || {})) {
    if (!g) continue;
    out.growers[propId] = {
      plantedAt: Number(g.plantedAt) || 0,
      collectedAt: Number(g.collectedAt) || Number(g.plantedAt) || 0,
    };
  }

  if (ITEMS[raw.sky]    && ITEMS[raw.sky].kind    === KIND.SKY)    out.sky = raw.sky;
  if (ITEMS[raw.ground] && ITEMS[raw.ground].kind === KIND.GROUND) out.ground = raw.ground;
  if (ITEMS[raw.hallFloor] && ITEMS[raw.hallFloor].kind === KIND.HALL_FLOOR) out.hallFloor = raw.hallFloor;
  if (ITEMS[raw.hallWall]  && ITEMS[raw.hallWall].kind  === KIND.HALL_WALL)  out.hallWall  = raw.hallWall;
  if (ITEMS[raw.hallLight] && ITEMS[raw.hallLight].kind === KIND.HALL_LIGHT) out.hallLight = raw.hallLight;

  // ⭐ OW-42 · the Hall's interior and saved designs, passed through RAW —
  // hallInterior.js normalises them (it imports this file; the reverse
  // would be a cycle). Consumers call normaliseInterior/normaliseDesigns.
  out.interiorRaw = raw.interior && typeof raw.interior === "object" ? raw.interior : null;
  out.designsRaw  = raw.designs && typeof raw.designs === "object" ? raw.designs : null;

  if (raw.companion && typeof raw.companion.beingId === "string") {
    out.companion = {
      beingId: raw.companion.beingId,
      tier: raw.companion.tier || "beb",
    };
  }
  return out;
}

// Resolve a prop's drawn position, walking up its stack. Returns tile
// x/y plus the height it sits at. The renderer calls this; nothing else
// needs to know how a stack is shaped.
export function resolvePlacement(props, propId, _depth = 0) {
  const p = props?.[propId];
  if (!p) return null;
  // ⚠ A CYCLE IS POSSIBLE IN PRINCIPLE (two props parented to each other
  // by a bad write) and it would be an infinite loop inside a frame
  // callback — a hard browser lock, not an error message. The depth
  // cap is what makes that impossible rather than unlikely.
  if (!p.on || _depth > 8) return { x: p.x, y: p.y, lift: 0, rot: p.rot };
  const base = resolvePlacement(props, p.on, _depth + 1);
  if (!base) return { x: p.x, y: p.y, lift: 0, rot: p.rot };
  const parentTop = ITEMS[props[p.on].itemId]?.stackTop || 0;
  return { x: base.x, y: base.y, lift: base.lift + parentTop, rot: p.rot };
}

// Everything stacked on top of this prop, in stack order.
export const childrenOf = (props, propId) =>
  // ⚠ `p &&` IS NOT DEFENSIVE PADDING. Firebase hands back a props bag
  // with a NULL entry for a moment after a delete, and a listener can
  // deliver it mid-pick-up — at which point this threw
  // `Cannot read properties of undefined (reading 'on')` INSIDE a
  // pick-up, aborting it halfway. Caught by a scripted test, not by a
  // parser: it type-checks perfectly.
  Object.values(props || {}).filter((p) => p && p.on === propId);

// ════════════════════════════════════════════════════════════════════
// §5 · ⭐ GROWTH
// ────────────────────────────────────────────────────────────────────
// The whole model in one paragraph: a grower ripens every `ripenMs`,
// each ripe cycle is worth `yieldUD`, and the barn holds `capCycles`
// of them before it stops filling. Come back inside eight hours and you
// lose nothing; leave it a week and you collect eight hours' worth.
// caP'n: "fills up and stops — that's the thing that makes you want to
// come back."
// ════════════════════════════════════════════════════════════════════

// ⚠⚠ THE OVERFLOW TRAP, WRITTEN OUT BECAUSE IT IS AN INFINITE MONEY BUG
// AND IT LOOKS COMPLETELY CORRECT.
//
// The obvious harvest is: cycles = min(elapsed/ripen, cap), then push
// the clock forward by exactly what you paid — `collectedAt +=
// cycles * ripenMs`. That is right while the barn is filling, and it is
// catastrophic once it has overflowed. Leave a 5-minute bed for three
// days: raw = 864 cycles, capped to 96, and the clock advances by only
// 96 cycles — leaving it still 768 cycles in the past. Harvest again a
// second later and it pays another full barn. And again. And again.
//
// So: WHEN THE BARN OVERFLOWED, THE CLOCK RESETS TO NOW. The overflow
// is genuinely lost, which is exactly what "fills up and stops" means,
// and it is the only reading under which the cap is a cap at all.
// While the barn is merely filling, the clock advances by the paid
// cycles only, so the part-grown remainder is never quietly taken —
// same principle as THE DUST LAW (L10) at the Well.
export function growthOf(item, growerState, now = serverNow()) {
  const g = item?.grow;
  if (!g) return null;
  const last = Number(growerState?.collectedAt) || 0;
  if (!last) return { cycles: 0, ready: 0, capped: false, raw: 0, msToNext: g.ripenMs, fill: 0 };

  const elapsed = Math.max(0, now - last);
  const raw = Math.floor(elapsed / g.ripenMs);
  const cycles = Math.min(raw, g.capCycles);
  // ⚠ `>` NOT `>=` — an exactly-full barn has FILLED, it has not
  // OVERFLOWED, and only an overflow justifies discarding the remainder.
  // At `>=`, coming back at 8h59m on an 8-cycle/1h grower paid the same
  // as 8h00m and then reset the clock to now, silently burning 59
  // minutes of growth — so waiting LONGER left you strictly worse off,
  // with no message. Found by adversarial review 2026-09-13.
  const capped = raw > g.capCycles;

  return {
    raw,
    cycles,
    capped,
    ready: cycles * g.yieldUD,
    // how full the barn looks, 0..1 — the renderer's dial
    fill: Math.min(1, raw / g.capCycles),
    // ms until the next cycle lands (0 once the barn is full)
    msToNext: capped ? 0 : g.ripenMs - (elapsed % g.ripenMs),
  };
}

// What the whole holding is holding, unharvested.
export function pendingTotal(holding, now = serverNow()) {
  let total = 0;
  for (const [propId, p] of Object.entries(holding?.props || {})) {
    const item = ITEMS[p.itemId];
    if (!item || item.kind !== KIND.GROWER) continue;
    const g = growthOf(item, holding.growers?.[propId], now);
    total += g?.ready || 0;
  }
  return total;
}

// ════════════════════════════════════════════════════════════════════
// §6 · HARVEST
// ────────────────────────────────────────────────────────────────────
// ⚠ THIS ONE DELIBERATELY INVERTS "GRANT BEFORE FLAG", and the reason
// matters. That law protects a player from losing a reward they earned,
// so a faucet pays first and marks itself claimed second. Here the
// "flag" is not a marker — IT IS THE AMOUNT. Two tabs that both read a
// full barn before either writes would both compute the same full
// payout and both pay it.
//
// So the CLAIM goes first, as a transaction that atomically decides how
// much was in the barn and moves the clock in the same operation. Only
// then is the money paid. If the payment fails, the claim is ROLLED
// BACK to the timestamp it had, so the harvest is still there. That is
// the same charge → grant → refund ladder the shop uses, read from the
// other end.
// ════════════════════════════════════════════════════════════════════
export async function harvest(uid, propId) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, reason: "not_you", message: "You can only harvest your own holding." };
  }

  const propSnap = await get(ref(db, `${propsPath(uid)}/${propId}`)).catch(() => null);
  const prop = propSnap?.val();
  const item = prop && ITEMS[prop.itemId];
  if (!item || item.kind !== KIND.GROWER) {
    return { ok: false, reason: "not_a_grower", message: "Nothing to collect there." };
  }

  const now = serverNow();
  let claimed = 0;
  let previous = null;
  // What the transaction actually committed as the new clock — the only
  // honest anchor for a partial settle. See the note in the rollback.
  let wroteCollectedAt = null;

  // ⚠ A GROWER WITH NO CLOCK ROW WAS DEAD FOREVER (found 2026-09-15). The
  // transaction below synthesised `collectedAt: now`, saw zero cycles and
  // ABORTED — writing nothing — so a bed whose clock write had failed at
  // placement, or a row hand-deleted in the console, never grew and never
  // would. ⚠ And on a real Firebase the SAME path is reachable for a
  // perfectly good row: `runTransaction` runs its updater with `null` when
  // the node is not in the local cache, and `undefined` from the updater
  // is a final abort, not a retry. Warm the cache with a read first, and
  // if the row genuinely does not exist, SEED it so the grower is alive
  // from this moment on. One read; a dead barn is somebody's money.
  const growerRef = ref(db, `${growersPath(uid)}/${propId}`);
  try {
    const gSnap = await get(growerRef);
    if (!gSnap.exists()) {
      await update(growerRef, { plantedAt: serverTimestamp(), collectedAt: serverTimestamp() });
      return { ok: false, reason: "not_ready", message: "It has only just started. Give it a minute." };
    }
  } catch {
    return { ok: false, reason: "check_failed", message: "Could not reach the barn. Try again." };
  }

  const tx = await runTransaction(growerRef, (cur) => {
    const state = cur || { plantedAt: now, collectedAt: now };
    const g = growthOf(item, state, now);
    if (!g || g.cycles < 1) { claimed = 0; return; }      // abort — nothing ripe
    previous = state.collectedAt;
    claimed = g.ready;
    return {
      plantedAt: state.plantedAt || now,
      // THE OVERFLOW RULE, above. Full barn → the clock restarts now.
      collectedAt: (wroteCollectedAt = g.capped ? now : (state.collectedAt + g.cycles * item.grow.ripenMs)),
    };
  }).catch(() => null);

  if (!tx || !tx.committed || claimed <= 0) {
    return { ok: false, reason: "not_ready", message: "Not ready yet. Give it a minute." };
  }

  // ⚠ PAID IN WHOLE CYCLES (2026-09-15). `grantDawgs` chunks at the
  // ceiling in Dawgs, not in cycles — so a bite of ✹2,500 against a ✹95
  // tree paid for 26.3 cycles, the settle below floored it to 26, and the
  // player kept the 0.3 (✹30) on top of a barn that still stood 1 cycle
  // taller than it should. Bounded — never more than one yield per
  // partial — but "two harvests never exceed the barn" was false. So the
  // bites are cut HERE, on cycle boundaries, and the settle maths is exact
  // by construction. A yield larger than the ceiling (none today) still
  // goes through grantDawgs' own chunking one cycle at a time.
  const yieldUD = Math.max(1, item.grow.yieldUD || 1);
  const biteUD = Math.max(yieldUD, Math.floor(DAWGS_GRANT_CEILING / yieldUD) * yieldUD);
  let paid = 0, left = claimed;
  while (left > 0) {
    const bite = Math.min(left, biteUD);
    const got = await grantDawgs(uid, bite, `minerium:harvest:${prop.itemId}`);
    paid += got;
    left -= bite;
    if (got !== bite) break;          // a refused bite stops the ladder
  }
  if (paid !== claimed) {
    // ── THE ROLLBACK — ⚠⚠ ONLY WHEN NOTHING WAS PAID.
    // It used to roll back unconditionally, which on a PARTIAL payout
    // paid the player AND stood the whole barn back up: harvest, bank
    // the partial, harvest again, forever. `grantDawgs` chunks at a
    // ceiling (`DAWGS_GRANT_CEILING`), so a partial is a real, reachable
    // state the moment any barn out-grows it — and `BARN_HOURS` is
    // documented as the one number you turn to make barns bigger.
    // Found by adversarial review 2026-09-13.
    if (paid > 0) {
      // Advance the clock by exactly what was PAID FOR and leave the
      // rest standing. The player keeps their Dawgs, the barn keeps the
      // cycles nobody paid for, and neither side gains.
      // ⚠⚠ SETTLE AGAINST WHAT THE TRANSACTION WROTE, NOT AGAINST THE
      // OLD CLOCK. Anchoring to `previous` is correct only while the
      // barn was FILLING. On an OVERFLOWED barn `previous` is however
      // long the player was away — three days, a week — so this line
      // dragged the clock back into the deep past and the very next
      // harvest paid another full barn. That is THE OVERFLOW TRAP (L44)
      // re-entering through the compensation path, which is the one
      // place nobody re-reads.
      // ⛑ The tx already computed the correct new clock (`now` if the
      // barn overflowed, else advanced by the cycles). Back off from
      // THAT by exactly the cycles nobody paid for, and the arithmetic
      // is right in both states with no branch:
      //     filling   → wrote − unpaid = previous + paidCycles  (as before)
      //     overflowed→ now − unpaid   (bounded by the barn, not by time)
      // Found by adversarial review 2026-09-14.
      const cycleMs = item.grow.ripenMs;
      const paidCycles = Math.floor(paid / (item.grow.yieldUD || 1));
      const claimedCycles = Math.floor(claimed / (item.grow.yieldUD || 1));
      const unpaidCycles = Math.max(0, claimedCycles - paidCycles);
      const settled = (wroteCollectedAt ?? previous ?? 0) - unpaidCycles * cycleMs;
      await update(ref(db, `${growersPath(uid)}/${propId}`), { collectedAt: settled }).catch(() => {});
      return { ok: true, itemId: prop.itemId, paidUD: paid, partial: true,
               message: `Only ✹${paid} of ✹${claimed} came through. The rest is still in the ground.` };
    }
    if (previous !== null) {
      await update(ref(db, `${growersPath(uid)}/${propId}`), { collectedAt: previous }).catch(() => {});
    }
    return { ok: false, reason: "pay_failed", message: "The Dawgs did not arrive — your harvest is still standing. Try again." };
  }

  return { ok: true, itemId: prop.itemId, paidUD: paid };
}

// Collect every ripe grower in the holding, one at a time. Sequential
// on purpose: each is its own transaction and its own ledger row, and a
// partial failure must leave the rest of the barn standing rather than
// taking the whole holding down with it.
export async function harvestAll(uid, holding) {
  const results = [];
  let total = 0;
  const now = serverNow();
  for (const [propId, p] of Object.entries(holding?.props || {})) {
    const item = ITEMS[p.itemId];
    if (!item || item.kind !== KIND.GROWER) continue;
    const g = growthOf(item, holding.growers?.[propId], now);
    if (!g || g.cycles < 1) continue;
    const r = await harvest(uid, propId);
    results.push({ propId, ...r });
    if (r.ok) total += r.paidUD || 0;
  }
  return { ok: results.some((r) => r.ok), total, results, count: results.filter((r) => r.ok).length };
}

// ════════════════════════════════════════════════════════════════════
// §6b · THE SHED  —  what you are holding that you could put HERE
// ────────────────────────────────────────────────────────────────────
// ⭐ OW-24. One shed, two rooms. `MineriumScreen` filtered this inline
// on `AREAS.YARD`, and the Hall needs the same list filtered the other
// way — so it lives HERE, taking the area as an argument, rather than
// being written twice and drifting apart the first time an item's
// `areas` changes. The engine already REFUSES a wrong-area placement in
// `placeProp`; this is so the player is never OFFERED one, because a
// button whose only job is to refuse you is a worse answer than no
// button at all.
// ════════════════════════════════════════════════════════════════════
export function shedFor(stock, area = AREAS.YARD) {
  return Object.entries(stock || {})
    .filter(([, n]) => Number(n) > 0)
    .map(([id, n]) => (ITEMS[id] ? { ...ITEMS[id], count: Math.floor(Number(n)) } : null))
    .filter((i) => i && i.areas?.includes(area))
    .sort((a, b) =>
      SHELVES.indexOf(a.shelf) - SHELVES.indexOf(b.shelf) ||
      a.name.localeCompare(b.name));
}

// Everything standing in ONE room. Both renderers take this, never the
// raw props bag, so neither surface can draw the other one's furniture.
export const propsInArea = (props, area) =>
  Object.values(props || {}).filter((p) => p && (p.area || AREAS.YARD) === area);

// ════════════════════════════════════════════════════════════════════
// §7 · PLACEMENT
// ════════════════════════════════════════════════════════════════════

// Does an item of this footprint at (x, y) overlap anything already
// standing? Stacked props are excluded — they are ON something, not
// beside it, and two things sharing a tile vertically is the point.
// ⚠⚠ AREA-AWARE SINCE OW-24, AND IT HAD TO BECOME SO. The Hall and the
// yard are two SEPARATE tile spaces that both start at (0,0) — they are
// not two regions of one map. Before this argument existed, a chair at
// hall (3,4) and a crop bed at yard (3,4) collided with each other, and
// the symptom is the worst kind: the player is told "a Crop Bed is
// already there" while standing in a room that visibly has no crop bed
// in it. Every caller passes the area it is placing INTO, and a prop in
// the other area is not in the way because it is not in the room.
// ⭐ THE BOUNDS QUESTION, ASKED FOR THE WHOLE PIECE AND NOT ITS PIN.
// ⚠⚠ `inArea` tests ONE POINT — the centre — which is right for a
// one-tile piece and wrong for every footprint-2 one in the catalogue.
// An Anima Tree centred on the yard's last legal tile passed the bounds
// test and then stood a full tile-column of trunk THROUGH the fence and
// out over the void; a Throne of Dawgs centred beside the Deed Book
// spanned straight across the fixture the reserved-tile set exists to
// keep clear. Both are invisible to a centre test, by construction.
// ⛑ Only the extent BEYOND the ordinary half-tile is checked, so a
// footprint-1 piece asks exactly the question it asked before and
// nothing that is standing today becomes illegal.
// Found by adversarial review 2026-09-14.
export const spanOf = (footprint = 1) => Math.max(0, (footprint || 1) / 2 - 0.5);

export function fitsInArea(area, x, y, footprint = 1) {
  const { w, d } = asDims(footprint);
  const ex = spanOf(w), ey = spanOf(d);
  return inArea(area, x - ex, y - ey) && inArea(area, x + ex, y + ey);
}

// Every tile a piece actually covers — for callers with their own floor
// rules to check (the Hall's `isWalkable` / reserved fixtures).
//
// ⚠⚠ IT MUST RETURN **INTEGER TILES**, and the first version did not.
// It stepped ±`spanOf()` which is 0.5 for a footprint-2 piece, so it
// handed back (5.5, 3.5) — coordinates that are not tiles. `PierHome`'s
// `isWalkable` then did `MAP[3.5][5.5]`, and `MAP[3.5]` is `undefined`,
// so picking up any of the six footprint-2 items in the Hall threw
// **inside a useMemo, during render** — a white screen. And where it did
// not throw it silently did nothing: `isReservedTile` builds the key
// "5.5,3.5", which is in no Set, so the Deed Book was still not
// protected — the exact bug the function was written to fix.
// ⛑ A piece centred at `x` spans [x − fp/2, x + fp/2]; tile `t` is the
// unit square [t − 0.5, t + 0.5]. They overlap when |t − x| ≤ e + 0.5.
// That is symmetric about the centre, correct for half-tile snapping
// (SNAP is 0.5), and agrees with what `collides()` already believes.
// Found by adversarial review of the fix itself, 2026-09-14.
export function tilesUnder(x, y, footprint = 1) {
  const { w, d } = asDims(footprint);
  const ex = spanOf(w) + 0.5 + 1e-6, ey = spanOf(d) + 0.5 + 1e-6;
  const out = [];
  for (let tx = Math.ceil(x - ex); tx <= Math.floor(x + ex); tx++) {
    for (let ty = Math.ceil(y - ey); ty <= Math.floor(y + ey); ty++) out.push([tx, ty]);
  }
  return out;
}

// ⭐ OW-34: `layer` — pieces only collide with their own layer (see LAYER
// in the catalogue). `null` keeps the old answer: every layer collides.
// ⚑ The other piece's shape is read TURNED, from its own row and rot.
export function collides(props, x, y, footprint = 1, ignorePropId = null, area = AREAS.YARD, layer = null) {
  const { w, d } = asDims(footprint);
  for (const p of Object.values(props || {})) {
    if (!p) continue;                      // see childrenOf — same null bag
    if (p.propId === ignorePropId) continue;
    if (p.on) continue;
    if ((p.area || AREAS.YARD) !== area) continue;
    if (layer && layerOf(p.itemId) !== layer) continue;
    const o = dimsOf(p.itemId, p.rot);
    if (Math.abs(p.x - x) < (w + o.w) / 2 - 0.01 && Math.abs(p.y - y) < (d + o.d) / 2 - 0.01) return p;
  }
  return null;
}

// Can `itemId` be stacked onto `targetPropId`?
export function canStackOn(props, targetPropId, itemId, area = null) {
  const target = props?.[targetPropId];
  if (!target) return { ok: false, reason: "The ground is not a shelf." };
  // ⚠ OW-24. A stack inherits its base's POSITION, so stacking across
  // areas would teleport the piece into the other room at the base's
  // coordinates — and it would look, from the shed, exactly like the
  // piece was simply eaten.
  if (area && (target.area || AREAS.YARD) !== area) {
    return { ok: false, reason: "That is in the other half of your holding." };
  }
  const targetItem = ITEMS[target.itemId];
  if (!targetItem || targetItem.stackTop == null) {
    return { ok: false, reason: `Nothing balances on ${targetItem?.name || "that"}.` };
  }
  const child = ITEMS[itemId];
  if (!child) return { ok: false, reason: "No such piece." };
  // ⚠ A GROWER NEEDS THE GROUND (2026-09-15). A crop bed balanced on a
  // barrel passed every test and looked like a bug in the barrel; and a
  // bed's clock lives by propId, so it harvests fine up there — which is
  // exactly why nothing ever refused it.
  if (child.kind === KIND.GROWER) return { ok: false, reason: `${child.name} needs the ground under it.` };
  // ⚠ FLAT ON FLAT IS NOT A STACK (2026-09-15). A stone path accepts a
  // stack (a chair on a rug is the whole point of a rug), so laying a
  // path put slab two ON slab one and the ghost said "BALANCE IT" after
  // every tile. Two walk-over pieces go beside each other, never on top.
  if (child.walkOver && targetItem.walkOver) {
    return { ok: false, reason: `Lay the ${child.name} beside the ${targetItem.name}, not on it.` };
  }
  // ⭐ OW-34 · a rug goes on the floor and a banner goes on a wall — neither
  // balances on anything, and nothing balances on them.
  if (layerOf(itemId) !== LAYER.STAND) {
    return { ok: false, reason: layerOf(itemId) === LAYER.WALL
      ? `${child.name} hangs on a wall.` : `${child.name} goes on the floor.` };
  }
  if (layerOf(target.itemId) !== LAYER.STAND) {
    return { ok: false, reason: `Nothing balances on ${targetItem.name}.` };
  }
  // ⚠ NOTHING WIDER THAN ITS BASE. A two-tile pool on a one-tile barrel is
  // a physics joke, and `collides` skips stacked pieces — so the pool's
  // other tile would have been invisible to every placement test.
  // ⚑ OW-34: the child's LONGEST side against the base's SHORTEST, so a
  // chair still goes on a bench and a partition never goes on a barrel.
  {
    const [cw, cd] = sizeOf(itemId), [bw, bd] = sizeOf(target.itemId);
    if (Math.max(cw, cd) > Math.min(bw, bd)) {
      return { ok: false, reason: `${child.name} is too big to balance on ${targetItem.name}.` };
    }
  }
  // ⚠ ONE CHILD PER BASE. `collides()` deliberately skips anything with
  // an `on`, and every sibling inherits the SAME x/y/lift from
  // `resolvePlacement` — so without this check you can stack four
  // torches on one barrel, see exactly one of them, and quietly lose the
  // other three inside it. Found by adversarial review 2026-09-13.
  if (childrenOf(props, targetPropId).length) {
    return { ok: false, reason: `Something is already balanced on ${targetItem.name}.` };
  }

  // A tower of four is charming. A tower of forty is a physics bug.
  let depth = 0, cur = target;
  while (cur?.on && depth < 8) { cur = props[cur.on]; depth++; }
  if (depth >= 3) return { ok: false, reason: "That stack is tall enough." };
  return { ok: true };
}

// ⭐ THE GHOST'S THREE ANSWERS (2026-09-15). The ghost used to know two
// colours, and the red one LIED: `ghostBlocked` was true for any
// collision — including a barrel you could balance a torch on — while E
// went ahead and stacked it anyway. "BLOCKED" on a spot that accepts the
// piece, and E doing something the prompt said it would not, is the
// worst kind of wrong: it teaches the player the prompt is decoration.
//   free    — the ground takes it
//   stack   — something is there and the piece balances on it (`on`)
//   blocked — it cannot go there, and `reason` says why
// `floorOk(tx, ty)` is the caller's own floor rule (the Hall's walls,
// fixtures and reserved tiles); the yard passes nothing.
// ⭐ OW-34: `rot` (the held angle) turns the shape; `floorOk(tx, ty, ctx)`
// now also hears `ctx = { itemId, layer, rot }`, so the Hall can say where
// a banner may hang. A wall piece has no floor rule of its own in here —
// the engine does not know where the walls are; the room does.
export function ghostStateFor(props, area, x, y, itemId, { ignorePropId = null, floorOk = null, rot = 0 } = {}) {
  const item = ITEMS[itemId];
  if (!item) return { state: "blocked", reason: "No such piece.", on: null };
  if (!item.areas?.includes(area)) {
    return { state: "blocked", on: null,
             reason: area === AREAS.HALL ? `${item.name} belongs outside.` : `${item.name} belongs indoors.` };
  }
  const layer = layerOf(itemId);
  const fp = dimsOf(itemId, rot);
  if (!fitsInArea(area, x, y, fp)) {
    return { state: "blocked", on: null,
             reason: area === AREAS.HALL ? "That is through a wall." : "That is outside the fence." };
  }
  if (floorOk) {
    for (const [tx, ty] of tilesUnder(x, y, fp)) {
      const ok = floorOk(tx, ty, { itemId, layer, rot });
      if (ok !== true) return { state: "blocked", on: null, reason: typeof ok === "string" ? ok : "The Hall's own things stand there." };
    }
  }
  const hit = collides(props, x, y, fp, ignorePropId, area, layer);
  if (!hit) return { state: "free", on: null, reason: null };
  // a MOVE never stacks — the piece already exists and keeps its own tile;
  // and a rug or a hanging never stacks either, so say what is in the way
  if (ignorePropId || layer !== LAYER.STAND) {
    const verb = layer === LAYER.WALL ? "already hangs there" : "is already there";
    return { state: "blocked", on: null, reason: `${ITEMS[hit.itemId]?.name || "Something"} ${verb}.` };
  }
  const can = canStackOn(props, hit.propId, itemId, area);
  if (can.ok) return { state: "stack", on: hit.propId, reason: null, onName: ITEMS[hit.itemId]?.name || "that" };
  return { state: "blocked", on: null, reason: can.reason };
}

// ⭐ THE NEXT TILE ALONG (2026-09-15). After a piece goes down the ghost
// steps on in the direction you face, so laying twelve slabs is twelve
// presses of E and no walking. It skips a tile the piece cannot take —
// a slab set down on a stack target sits half a tile off the grid, and
// "one tile on" from there still overlaps the base.
export function nextAim(at, facing, props, itemId, area = AREAS.YARD, opts = {}) {
  const dx = facing?.x || 0, dy = (facing?.x || facing?.y) ? (facing.y || 0) : 1;
  // ⚑ OW-34: a long piece steps its own length, so a 1×3 runner laid
  // end-on lands end to end instead of overlapping the one just laid
  const { w, d } = dimsOf(itemId, opts.rot || 0);
  const stepX = (dx || 0) * (dx ? w : 1), stepY = dy * (dy ? d : 1);
  let fallback = null;
  for (let k = 1; k <= 3; k++) {
    const cand = { x: at.x + stepX * k, y: at.y + stepY * k };
    if (!fallback) fallback = cand;
    if (ghostStateFor(props, area, cand.x, cand.y, itemId, opts).state === "free") return cand;
  }
  return fallback;
}

// ⭐ THE YARD'S OWN FLOOR RULE — the spawn is where every visitor
// arrives, and a solid piece on it welds them to the porch (review,
// 2026-09-15). The engine refuses it in `placeProp` too; this is the
// ghost's copy of the same answer.
export const yardFloorOk = (x, y, ctx = null) =>
  (ctx && ctx.layer === LAYER.FLOOR) ? true :       // a rug on the spawn welds nobody
  (Math.abs(x - YARD.spawn.x) < 1 && Math.abs(y - YARD.spawn.y) < 1) ? "That is where you arrive." : true;

// ⭐ SOLID FURNITURE (2026-09-15). caP'n's ask was "a proper building
// game", and in every one of them a pillar is a thing you walk AROUND.
// Both rooms used to let you stroll through a Pale Pillar — furniture as
// hologram, which is the single loudest "this is not a building game yet"
// tell. A piece is solid unless its catalogue row says `walkOver: true`
// (rugs, paths, beds, doors — things you are supposed to stand on or
// through). Stacked pieces are not tested — they sit on something that is.
// ⚑ The caller never tests the tile it is STANDING on, only where it is
// going, so a player can always step back the way they came and can
// never be welded in by their own chair.
export function blockedByProp(props, area, x, y, radius = 0.3, ignorePropId = null) {
  for (const p of Object.values(props || {})) {
    if (!p || p.on) continue;
    if (ignorePropId && p.propId === ignorePropId) continue;   // the piece being MOVED is not there
    if ((p.area || AREAS.YARD) !== area) continue;
    const item = ITEMS[p.itemId];
    if (!item || item.walkOver) continue;
    const { w, d } = dimsOf(p.itemId, p.rot);
    if (Math.abs(p.x - x) < w / 2 + radius - 0.02 && Math.abs(p.y - y) < d / 2 + radius - 0.02) return p;
  }
  return null;
}

// ⭐ OW-34 · THE HALL'S GRID FOR A TURNED PIECE. The Hall is an integer grid:
// an odd extent is centred on a tile, an even one on a tile corner. A 2×1
// screen at (5.5, 6) turned a quarter is 1×2, and left where it was it sat
// across tile seams on BOTH axes. It pivots on its first tile instead:
// (5.5, 6) → (5, 6.5), and back again. The yard's half-tile grid needs none.
export function hallCentre(x, y, dims) {
  const { w, d } = asDims(dims);
  const half = (v) => Math.abs(v - Math.round(v)) > 0.25;
  let nx = x, ny = y;
  if (half(nx) !== (w % 2 === 0)) nx = half(nx) ? nx - 0.5 : nx + 0.5;
  if (half(ny) !== (d % 2 === 0)) ny = half(ny) ? ny - 0.5 : ny + 0.5;
  return { x: nx, y: ny };
}

// The raw rows still carrying `on: <a floor piece>` (see normaliseHolding),
// set down where they stand, in the same write as the thing that would
// otherwise strand them. Best-effort: a failed read changes nothing.
async function healFloorChildren(uid, baseId, at) {
  try {
    const snap = await get(ref(db, propsPath(uid)));
    const raw = snap.val() || {};
    const patch = {};
    for (const [id, r] of Object.entries(raw)) {
      if (r && r.on === baseId) {
        patch[`${id}/on`] = null;
        patch[`${id}/x`] = at.x;
        patch[`${id}/y`] = at.y;
      }
    }
    return patch;
  } catch { return {}; }
}

// ⚠⚠ A MOVE OR A TURN IS WRITTEN IN A TRANSACTION (E2E review, 2026-09-16).
// A plain `update` on a row another tab had just lifted RECREATED it —
// `{ x, y, rot }` with no itemId, invisible and in the database forever —
// and a move on a row somebody was mid-lift raced the pick-up. The row must
// still exist and must not be claimed (L68). An old rug-stack loses its
// `on` in the same write, or the loader pins it back onto the rug.
export const LIFT_TTL_MS = 30 * 1000;
async function writeRow(uid, propId, patch, { dropOn = false } = {}) {
  const now = serverNow();
  let why = null;
  const tx = await runTransaction(ref(db, `${propsPath(uid)}/${propId}`), (cur) => {
    if (!cur || !cur.itemId) { why = "gone"; return; }
    if (cur.lifting && now - Number(cur.lifting) < LIFT_TTL_MS) { why = "lifting"; return; }
    const next = { ...cur, ...patch };
    if (dropOn) delete next.on;
    return next;
  }).catch(() => null);
  if (tx && tx.committed) return { ok: true };
  if (why === "gone") return { ok: false, reason: "no_such_prop", message: "That is not there any more." };
  if (why === "lifting") return { ok: false, reason: "contested", message: "Somebody is lifting that right now." };
  return { ok: false, reason: "write_failed", message: "It would not move. Try again." };
}

// the yard's own floor rule, for anything that lands without a ghost
const defaultFloorOk = (area, itemId) =>
  (area === AREAS.YARD && !ITEMS[itemId]?.walkOver ? yardFloorOk : null);

// ── PLACE ───────────────────────────────────────────────────────────
// The ladder: take from stock (transaction, aborts at zero) → write the
// prop → on failure, put the stock back. Exactly the shop's shape, and
// for the same reason: the failure to defend against is an item that
// left the shed and never appeared in the world.
export async function placeProp(uid, { itemId, area = AREAS.YARD, x, y, rot = 0, on = null }, props = {}, { floorOk = null } = {}) {
  if (!uid || auth.currentUser?.uid !== uid) {
    return { ok: false, reason: "not_you", message: "You can only build in your own holding." };
  }
  const item = ITEMS[itemId];
  if (!item) return { ok: false, reason: "no_such_item", message: "No such piece." };
  if (isWorn(itemId)) return { ok: false, reason: "worn", message: "That one is worn over the holding, not put down in it." };
  if (!item.areas?.includes(area)) {
    return { ok: false, reason: "wrong_area",
             message: area === AREAS.HALL ? `${item.name} belongs outside.` : `${item.name} belongs indoors.` };
  }

  let px = snap(x), py = snap(y);
  const layer = layerOf(itemId);
  // ⭐ OW-34 · a non-square or wall piece only ever lands on a quarter
  if (turnsInQuarters(itemId)) rot = quarterRot(rot);
  const dims = dimsOf(itemId, rot);
  if (area === AREAS.HALL && !on) ({ x: px, y: py } = hallCentre(px, py, dims));
  // ⚠ OW-34 · UNDO OF AN OLD RUG-STACK. The undo entry says `on: <rug>`;
  // a rug is floor now and takes no stack, so the piece simply stands there.
  if (on && props?.[on] && layerOf(props[on].itemId) !== LAYER.STAND) {
    const b = resolvePlacement(props, on);
    if (b) { px = b.x; py = b.y; }
    on = null;
  }

  if (on) {
    const can = canStackOn(props, on, itemId, area);
    if (!can.ok) return { ok: false, reason: "cannot_stack", message: can.reason };
    const base = resolvePlacement(props, on);
    px = base?.x ?? px; py = base?.y ?? py;   // a stacked prop inherits its base
  } else {
    if (!fitsInArea(area, px, py, dims)) {
      return { ok: false, reason: "out_of_bounds",
               message: area === AREAS.HALL ? "That is through a wall." : "That is outside the fence." };
    }
    // the room's own floor rule — the yard's spawn by default, the Hall's
    // walls, fixtures and hangings when the Hall passes its own (an UNDO has
    // no ghost to have asked it first)
    const rule = floorOk || defaultFloorOk(area, itemId);
    if (rule) {
      for (const [tx, ty] of tilesUnder(px, py, dims)) {
        const ok = rule(tx, ty, { itemId, layer, rot });
        if (ok !== true) return { ok: false, reason: "reserved", message: typeof ok === "string" ? ok : "It cannot go there." };
      }
    }
    const hit = collides(props, px, py, dims, null, area, layer);
    if (hit) {
      const hitName = ITEMS[hit.itemId]?.name || "something";
      return { ok: false, reason: "occupied", message: `${hitName} is already there. Stack it instead?`, blockedBy: hit.propId };
    }
  }

  // 1 · take one out of the shed
  let took = false;
  const tx = await runTransaction(ref(db, `${stockPath(uid)}/${itemId}`), (cur) => {
    const n = Number(cur) || 0;
    if (n < 1) return;                 // abort, do not clamp
    took = true;
    return n - 1;
  }).catch(() => null);
  if (!tx || !tx.committed || !took) {
    return { ok: false, reason: "no_stock", message: `You do not have a ${item.name} to put down.` };
  }

  // 2 · stand it up
  // ⭐ Hoisted OUT of the try so the compensation below can reach it.
  const node = push(ref(db, propsPath(uid)));
  try {
    // ⚠⚠ THE GROWER CLOCK IS WRITTEN **BEFORE** THE PROP, AND THAT ORDER
    // IS THE FIX. It used to be written after, inside this same `try`,
    // whose `catch` refunded only the STOCK — so a failure on the grower
    // write left the bed STANDING in the world and ALSO put it back in
    // your shed. Place, fail, repeat: unlimited crop beds, every
    // duplicate with no clock, so `growthOf` saw `last = 0` and it never
    // grew. A paid-for bed that can never grow, duplicated for free.
    //
    // ⛑ Written first, the worst case is an orphan clock under a propId
    // that never became a prop — invisible, inert, and unreachable
    // forever because push() never reissues a key. Written last, the
    // worst case is a duplication exploit. That asymmetry is the whole
    // argument. Found by adversarial review 2026-09-13.
    if (item.kind === KIND.GROWER) {
      await update(ref(db, `${growersPath(uid)}/${node.key}`), {
        plantedAt: serverTimestamp(),
        collectedAt: serverTimestamp(),
      });
    }

    const row = { itemId, area, x: px, y: py, rot: Number(rot) || 0 };
    if (on) row.on = on;
    await update(ref(db, `${propsPath(uid)}/${node.key}`), row);

    return { ok: true, propId: node.key, itemId, x: px, y: py };
  } catch {
    // 3 · take down anything that DID land, then put it back in the shed.
    // Order matters here too: remove first, so a player can never see the
    // piece in both places at once.
    await remove(ref(db, `${propsPath(uid)}/${node.key}`)).catch(() => {});
    await remove(ref(db, `${growersPath(uid)}/${node.key}`)).catch(() => {});
    await runTransaction(ref(db, `${stockPath(uid)}/${itemId}`), (cur) => (Number(cur) || 0) + 1).catch(() => {});
    return { ok: false, reason: "write_failed", message: "It would not stand up. Nothing was used." };
  }
}

// ── MOVE ────────────────────────────────────────────────────────────
// No stock moves, so no ladder — one write, and everything stacked on
// it comes along because children read their base's position rather
// than storing their own.
export async function moveProp(uid, propId, x, y, props = {}, rot = null, { floorOk = null } = {}) {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false, reason: "not_you" };
  const p = props[propId];
  if (!p) return { ok: false, reason: "no_such_prop" };
  if (p.on) return { ok: false, reason: "stacked", message: "Take it off the stack first." };

  // ⚠ OW-24. A move NEVER changes which area a prop is in — it slides
  // within the room it is already standing in. Carrying a chair from the
  // Hall out to the yard is a PICK UP and a PLACE, so that it goes back
  // through the shed and through `placeProp`'s area check, where the
  // "does this piece even belong outside" rule actually lives.
  const area = p.area === AREAS.HALL ? AREAS.HALL : AREAS.YARD;
  if (rot != null && Number.isFinite(rot) && turnsInQuarters(p.itemId)) rot = quarterRot(rot);
  const dims = dimsOf(p.itemId, rot != null && Number.isFinite(rot) ? rot : p.rot);
  if (!floorOk) floorOk = defaultFloorOk(area, p.itemId);
  let px = snap(x), py = snap(y);
  if (area === AREAS.HALL) ({ x: px, y: py } = hallCentre(px, py, dims));
  if (!fitsInArea(area, px, py, dims)) {
    return { ok: false, reason: "out_of_bounds",
             message: area === AREAS.HALL ? "That is through a wall." : "That is outside the fence." };
  }
  // ⚠ OW-34: the room's own floor rule, when the caller has one (the Hall's
  // walls and fixtures). The ghost asked it; the write asks it again.
  if (floorOk) {
    for (const [tx, ty] of tilesUnder(px, py, dims)) {
      const ok = floorOk(tx, ty, { itemId: p.itemId, layer: layerOf(p.itemId), rot: rot ?? p.rot });
      if (ok !== true) return { ok: false, reason: "reserved", message: typeof ok === "string" ? ok : "It cannot go there." };
    }
  }
  const hit = collides(props, px, py, dims, propId, area, layerOf(p.itemId));
  if (hit) return { ok: false, reason: "occupied", message: `${ITEMS[hit.itemId]?.name || "Something"} is already there.` };

  // ⚠ THE ROTATION PROMISE. A move shows a ghost, the prompt says "R
  // turns it", the ghost visibly turns — and the piece used to land at
  // whatever angle it already had, because `rot` was not a parameter at
  // all. Three affordances and a lie is worse than no affordance.
  // `null` keeps the existing angle, so old callers are unchanged.
  const patch = { x: px, y: py };
  if (rot != null && Number.isFinite(rot)) patch.rot = ((Math.round(rot / ROT_STEP) % ROT_STEPS) + ROT_STEPS) % ROT_STEPS * ROT_STEP;

  // a floor piece moving leaves any old rug-stack standing where it was
  const heal = layerOf(p.itemId) !== LAYER.STAND ? await healFloorChildren(uid, propId, { x: p.x, y: p.y }) : {};
  if (Object.keys(heal).length) {
    try { await update(ref(db, propsPath(uid)), heal); }
    catch { return { ok: false, reason: "write_failed" }; }
  }
  const w = await writeRow(uid, propId, patch, { dropOn: !!p.legacyOn });
  if (!w.ok) return w;
  return { ok: true, propId, x: px, y: py, rot: patch.rot };
}

// ⭐ OW-34 · A TURN IS A PLACEMENT when the piece is not square: a 3×1
// partition turned becomes a 1×3 one, and it may now run into a pillar or
// through a wall. So a turn that changes the shape asks the same questions
// a move does, and refuses with the reason. A square piece turns freely,
// exactly as before. A wall piece faces the room — it does not turn.
export async function rotateProp(uid, propId, steps = 1, props = {}, { floorOk = null } = {}) {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false, reason: "not_you" };
  const p = props[propId];
  if (!p) return { ok: false, reason: "no_such_prop" };
  if (layerOf(p.itemId) === LAYER.WALL) {
    return { ok: false, reason: "wall", message: `${ITEMS[p.itemId]?.name || "It"} faces the room. Move it to another wall instead.` };
  }
  // the Hall turns in quarters (rotStepFor); every step is a whole ROT_STEP unit
  const unit = Math.round(rotStepFor(p.area, p.itemId) / ROT_STEP) * steps;
  const base = turnsInQuarters(p.itemId) ? Math.round(quarterRot(p.rot) / ROT_STEP) : Math.round(p.rot / ROT_STEP);
  const next = (((base + unit) % ROT_STEPS) + ROT_STEPS) % ROT_STEPS;
  const [sw, sd] = sizeOf(p.itemId);
  const at = { x: p.x, y: p.y };
  if (sw !== sd && !p.on) {
    const area = p.area === AREAS.HALL ? AREAS.HALL : AREAS.YARD;
    const dims = dimsOf(p.itemId, next * ROT_STEP);
    if (area === AREAS.HALL) Object.assign(at, hallCentre(p.x, p.y, dims));
    if (!floorOk) floorOk = defaultFloorOk(area, p.itemId);
    const say = (message) => ({ ok: false, reason: "no_room", message });
    if (!fitsInArea(area, at.x, at.y, dims)) return say("There is no room to turn it — it would go through a wall.");
    if (floorOk) {
      for (const [tx, ty] of tilesUnder(at.x, at.y, dims)) {
        const ok = floorOk(tx, ty, { itemId: p.itemId, layer: layerOf(p.itemId), rot: next * ROT_STEP });
        if (ok !== true) return say(typeof ok === "string" ? `No room to turn it: ${ok}` : "There is no room to turn it.");
      }
    }
    const hit = collides(props, at.x, at.y, dims, propId, area, layerOf(p.itemId));
    if (hit) return say(`There is no room to turn it — the ${ITEMS[hit.itemId]?.name || "piece beside it"} is in the way.`);
  }
  const turned = { rot: next * ROT_STEP };
  if (at.x !== p.x || at.y !== p.y || p.legacyOn) { turned.x = at.x; turned.y = at.y; }
  const w = await writeRow(uid, propId, turned, { dropOn: !!p.legacyOn });
  if (!w.ok) return w;
  // `from` is what an UNDO needs: the angle and the spot it turned from
  return { ok: true, propId, rot: next * ROT_STEP, x: at.x, y: at.y, from: { x: p.x, y: p.y, rot: p.rot || 0 } };
}

// ── PICK UP ─────────────────────────────────────────────────────────
// ⚠ CHILDREN ARE RE-PARENTED BEFORE THE PARENT GOES, not after and not
// never. Picking up the barrel a torch is standing on must leave the
// torch standing where the barrel was — losing somebody's furniture
// because they moved the thing under it is the kind of bug that makes a
// player stop decorating.
export async function pickUpProp(uid, propId, props = {}, { floorOk = null } = {}) {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false, reason: "not_you" };
  const p = props[propId];
  if (!p) return { ok: false, reason: "no_such_prop" };

  // ⚠⚠ RIPE GROWERS ARE NOT PICKED UP — THEY ARE COLLECTED FIRST.
  // *(found 2026-09-13 reviewing OW-24, and it was live in the yard too)*
  // This function deletes the prop AND its `growers/{propId}` row, and
  // the row is where `collectedAt` lives — so lifting a full Dawg Bowl
  // DESTROYED every unharvested ✹ in it, with no message, no confirm and
  // no way to get it back. ⛑ SILENT LOSS IS THE WORST FAILURE SHAPE IN
  // THIS CODEBASE (the April 2026 wipe is why), and "your barn is full"
  // is the exact moment a player is most likely to be rearranging.
  //
  // ⚠ IT REFUSES rather than auto-harvesting, deliberately. Harvesting
  // from inside a pick-up would put a CURRENCY WRITE inside a placement
  // ladder, where a half-failure leaves the Dawgs paid and the prop still
  // standing, or the prop gone and the Dawgs not paid. Refusing is an
  // answer, not an error — and it costs the player one keypress.
  // ⛑ The grower row is READ FRESH rather than taken from the `props`
  // the caller handed in — `props` carries positions, not clocks, and the
  // clock is the whole question here. One extra read on a pick-up is
  // nothing next to silently deleting somebody's barn.
  {
    const item = ITEMS[p.itemId];
    if (item?.kind === KIND.GROWER) {
      try {
        const snap = await get(ref(db, `${growersPath(uid)}/${propId}`));
        const g = growthOf(item, snap.val(), serverNow());
        if (g && g.ready > 0) {
          return { ok: false, reason: "unharvested",
                   message: `Collect the ✹${g.ready} standing in the ${item.name} first — lifting it now would lose it.` };
        }
      } catch {
        // ⚠ A READ THAT FAILS MUST NOT BECOME A DELETE. If we cannot
        // tell whether the barn is full, we do not empty it.
        return { ok: false, reason: "check_failed",
                 message: "Could not check what is standing in it. Try again." };
      }
    }
  }

  // ⚠⚠ THE CLAIM (2026-09-15). The shed was credited before the prop was
  // removed — the right order, see below — but nothing stopped TWO clients
  // from doing it for the same prop: a phone and a desktop both showing
  // the chair, both pressing PICK UP, both increments landing, one remove
  // a no-op. Stock +2 for one chair. So the prop row is claimed FIRST, in
  // a transaction that aborts if somebody else holds it. A crash after the
  // claim leaves a claimed prop standing — it renders normally (unknown
  // fields are ignored on the way in) and the claim EXPIRES after
  // LIFT_TTL_MS, so it can be picked up again. Never a lock without a key.
  // ⚠ SERVER time (E2E review): a device whose clock ran 30 s fast read
  // every other device's claim as already expired
  const liftNow = serverNow();
  const claim = await runTransaction(ref(db, `${propsPath(uid)}/${propId}`), (cur) => {
    if (!cur) return;                                            // already gone
    if (cur.lifting && liftNow - Number(cur.lifting) < LIFT_TTL_MS) return;   // somebody has it
    return { ...cur, lifting: liftNow };
  }).catch(() => null);
  if (!claim || !claim.committed) {
    return { ok: false, reason: "contested", message: "Somebody is already lifting that." };
  }
  const unclaim = () => update(ref(db, `${propsPath(uid)}/${propId}`), { lifting: null }).catch(() => {});

  const here = resolvePlacement(props, propId) || { x: p.x, y: p.y };
  const kids = childrenOf(props, propId);

  // ⚠⚠ AN ORPHAN LANDS SOMEWHERE **LEGAL**, or it lands on the base's
  // own tile. This was the one write in the file that set an x/y without
  // going through `placeProp`'s gate: no bounds test, no collision test.
  // It offset each child by +0.5y, which in the yard pushed a child past
  // `YARD.y1` and in the Hall could drop a torch onto the Deed Book's
  // tile — where it drew over a working fixture and could never be
  // picked up again, because every tile you could reach it from claims
  // the Deed Book's prompt first. Found by adversarial review 2026-09-13.
  //
  // ⛑ THE FALLBACK IS THE BASE'S OWN TILE, deliberately. Two pieces
  // sharing a tile looks untidy and is fixed by dragging one; a piece
  // outside the fence or welded to a fixture is not fixable by anyone.
  const area = p.area === AREAS.HALL ? AREAS.HALL : AREAS.YARD;
  // a rug lifted leaves any old rug-stack standing where it was (OW-34)
  const patch = layerOf(p.itemId) !== LAYER.STAND ? await healFloorChildren(uid, propId, here) : {};
  const taken = [];
  // ⚑ the Hall is an integer grid — an orphan at (x, 11.5) is on no tile
  const orphanStep = area === AREAS.HALL ? 1 : SNAP;
  for (const k of kids) {
    let kx = here.x, ky = here.y;
    // ⚠ OW-34 (E2E review): the search reaches past the BASE's own extent —
    // the base is still in `props`, so every spot over it collides — and
    // tries all four ways, so a chair on a 2-wide bench lands BESIDE the
    // bench, and undoing the bench's pick-up is not refused by its chair.
    const kd = dimsOf(k.itemId, k.rot);
    const reach = Math.ceil((Math.max(...Object.values(dimsOf(p.itemId, p.rot))) + Math.max(kd.w, kd.d)) / 2 / orphanStep) + kids.length + 1;
    search:
    for (let step = 1; step <= reach; step++) {
      for (const [ux, uy] of [[0, 1], [1, 0], [-1, 0], [0, -1]]) {
        const tryX = here.x + ux * orphanStep * step, tryY = here.y + uy * orphanStep * step;
        if (!fitsInArea(area, tryX, tryY, kd)) continue;               // off the ground
        // the room's own floor rule (the Hall's walls and fixtures, verify review)
        if (floorOk && tilesUnder(tryX, tryY, kd).some(([tx, ty]) => floorOk(tx, ty, { itemId: k.itemId, layer: layerOf(k.itemId), rot: k.rot }) !== true)) continue;
        if (collides(props, tryX, tryY, kd, k.propId, area, layerOf(k.itemId))) continue;
        if (taken.some((t) => Math.abs(t.x - tryX) < 0.01 && Math.abs(t.y - tryY) < 0.01)) continue; // a sibling got it
        kx = tryX; ky = tryY;
        break search;
      }
    }
    taken.push({ x: kx, y: ky });
    // `on: null` is written explicitly rather than by removing the key,
    // so the whole re-parenting is one atomic update.
    patch[`${k.propId}/on`] = null;
    patch[`${k.propId}/x`]  = kx;
    patch[`${k.propId}/y`]  = ky;
  }

  // ⚠⚠ THE SHED IS CREDITED **FIRST**, AND THE ORDER IS THE FIX.
  // It used to remove the prop, then its grower row, then credit the
  // shed — three writes in one `try` that compensated for none of them.
  // A drop between the remove and the credit DESTROYED the item, and the
  // message said "It would not come up. Try again." — actively telling
  // the player nothing had happened while their Throne of Dawgs was gone
  // from the world and had never arrived in the shed. Silent, and
  // unrecoverable.
  //
  // ⛑ Credited first, the worst case is a DUPLICATE — visible, and undone
  // below. Deleted first, the worst case is a LOSS — invisible, and
  // undoable by nobody. Prefer the failure the player can see, every
  // time. Found by adversarial review 2026-09-13.
  let credited = false;
  try {
    // Back into the shed. An increment, never a read-then-write.
    await update(ref(db, stockPath(uid)), { [p.itemId]: increment(1) });
    credited = true;
    if (Object.keys(patch).length) await update(ref(db, propsPath(uid)), patch);
    await remove(ref(db, `${propsPath(uid)}/${propId}`));
    if (ITEMS[p.itemId]?.kind === KIND.GROWER) {
      await remove(ref(db, `${growersPath(uid)}/${propId}`)).catch(() => {});
    }
  } catch {
    // Hand the credit back, so a failure leaves the holding exactly as it
    // was rather than one piece richer — and let go of the claim.
    if (credited) {
      await update(ref(db, stockPath(uid)), { [p.itemId]: increment(-1) }).catch(() => {});
    }
    await unclaim();
    return { ok: false, reason: "write_failed", message: "It would not come up. Try again." };
  }
  return { ok: true, propId, itemId: p.itemId, orphaned: kids.length, x: here.x, y: here.y, rot: p.rot || 0, area, on: p.on || null };
}

// ════════════════════════════════════════════════════════════════════
// §8 · THE COMPANION
// ────────────────────────────────────────────────────────────────────
// caP'n: "we can have for now 1 Companion, not Pet… companions here will
// only use the Toonz from the obsidian vault, aka the character models
// in Annihilation and Rush."
//
// So the slot stores an ID and a tier — never a model. That is the same
// shape OW-5 (THE UNIVERSAL COMPANION ON THE MARK) is specified to
// need: "the slot stores an ID, and each surface renders that id in
// whatever it can… the moment the slot stores a MODEL instead of an ID,
// this becomes unshippable." This node is deliberately built to that
// spec so the Mark can adopt it later without a migration.
//
// ⚠ AND IT IS NOT `profiles/{uid}/companionId`. That field already
// exists and drives the 2D sprite that walks the pier beside you (see
// Pier3D's Actor). This is a different thing in a different place: a 3D
// being standing in your holding. PIERRE_PRIME §7 Q6 asks caP'n to rule
// on whether the Mark's companion is that field or a second one, and
// this file does not pre-empt that ruling — it keeps the Minerium's
// companion on the HOLDING, where a visitor can see it, and leaves the
// profile field exactly as it was.
// ════════════════════════════════════════════════════════════════════
export async function setCompanion(uid, beingId, tier = "beb") {
  if (!uid || auth.currentUser?.uid !== uid) return { ok: false, reason: "not_you" };
  if (!beingId) {
    try { await remove(ref(db, `${holdingPath(uid)}/companion`)); }
    catch { return { ok: false, reason: "write_failed" }; }
    return { ok: true, beingId: null };
  }
  try { await update(ref(db, `${holdingPath(uid)}/companion`), { beingId, tier }); }
  catch { return { ok: false, reason: "write_failed" }; }
  return { ok: true, beingId, tier };
}

const mineriumApi = {
  YARD, HALL, SNAP, snap, ROT_STEP, ROT_STEPS, rotStepFor, reachTile, inYard, inHall, inArea,
  dimsOf, quarterRot, asDims, tilesUnder, fitsInArea, hallCentre,
  shedFor, propsInArea,
  bindServerClock, serverNow, serverOffset,
  listenToHolding, emptyHolding, normaliseHolding,
  resolvePlacement, childrenOf, collides, canStackOn, ghostStateFor, blockedByProp, nextAim, yardFloorOk,
  growthOf, pendingTotal, harvest, harvestAll,
  placeProp, moveProp, rotateProp, pickUpProp, setCompanion,
  holdingPath,
};

export default mineriumApi;
